import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin } from "@phosphor-icons/react";
import { PageHero } from "../components/PageHero";
import { CTASection } from "../components/home/CTASection";
import { useProjects } from "../lib/api";

export default function ProjectsPage() {
  const { data: projects = [] } = useProjects();
  const [filter, setFilter] = useState("All");
  const categories = ["All", ...Array.from(new Set(projects.map((p) => p.category)))];
  const visible = filter === "All" ? projects : projects.filter((p) => p.category === filter);

  return (
    <div data-testid="projects-page">
      <PageHero
        overline="Portfolio"
        title="Work that speaks in concrete and steel."
        subtitle="A selection of redevelopment, restoration, and government projects across Detroit and Southeast Michigan."
      />

      <section className="bg-canvas py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-wrap gap-2 mb-12" data-testid="project-filters">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`project-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 text-xs font-mono-tech tracking-wider uppercase border transition-colors ${
                  filter === c
                    ? "bg-blueprint text-white border-transparent"
                    : "border-line text-sub hover:border-[var(--blueprint)] hover:text-blueprint"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visible.map((p, i) => (
              <motion.article
                key={p.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
                data-testid={`project-card-${i}`}
                className="group bg-canvas border border-line transition-all hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="relative h-56 overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <span className="absolute top-4 left-4 font-mono-tech text-[10px] tracking-[0.18em] uppercase bg-black/70 text-white px-2.5 py-1">
                    {p.category}
                  </span>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-xs text-sub font-mono-tech mb-2">
                    <MapPin size={13} className="text-blueprint" /> {p.location} · {p.year}
                  </div>
                  <h3 className="font-heading font-medium text-xl tracking-tight text-ink">{p.title}</h3>
                  <p className="mt-2 text-sm text-sub leading-relaxed">{p.description}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
